/**
 * The town map: every tile as a tiny diamond in the same isometric view as
 * the game, so the map looks like the place. Drawn once per session and
 * cached; markers (you, places, trams) go on a layer above it.
 */
import { DISTRICTS, footprint, grid, groundAt, ROOMS, type Place } from '../../../shared/world.ts'
import { GROUND_COLORS } from '../render/room.ts'

let cache: { canvas: HTMLCanvasElement; k: number; ox: number } | undefined

const TREES = new Set(['tree', 'pine', 'palm', 'willow', 'sakura', 'bamboo', 'bush', 'hedge'])

function base(width: number): { canvas: HTMLCanvasElement; k: number; ox: number } {
  if (cache && cache.canvas.width === width) return cache
  const r = ROOMS.town
  const g = grid('town')
  const k = width / (r.w + r.d)
  const c = document.createElement('canvas')
  c.width = width
  c.height = Math.ceil((r.w + r.d) * k / 2) + 4
  const ctx = c.getContext('2d')!
  const ox = r.d * k
  const tile = (x: number, y: number, color: string, s = 1) => {
    // Tile (x, y)'s diamond, centred, scaled by s.
    const cx = ox + (x - y) * k, cy = (x + y) * k / 2 + k / 2
    ctx.fillStyle = color
    ctx.beginPath(); ctx.moveTo(cx, cy - (k / 2) * s); ctx.lineTo(cx + k * s, cy); ctx.lineTo(cx, cy + (k / 2) * s); ctx.lineTo(cx - k * s, cy); ctx.closePath()
    ctx.fill()
  }
  for (let y = 0; y < r.d; y++) for (let x = 0; x < r.w; x++) tile(x, y, GROUND_COLORS[groundAt(g, x, y)])
  for (const p of r.props) {
    if (TREES.has(p.kind)) tile(p.x, p.y, p.kind === 'sakura' ? '#f2a6c0' : p.kind === 'hedge' ? '#3f8f5a' : '#4f9a5a', 0.8)
    else if (p.kind === 'building') {
      const f = footprint(p)
      for (let x = p.x; x < p.x + f.w; x++) for (let y = p.y; y < p.y + f.d; y++) tile(x, y, p.roof ?? '#c9705a')
    } else if (p.kind === 'pond') {
      const f = footprint(p)
      for (let x = p.x; x < p.x + f.w; x++) for (let y = p.y; y < p.y + f.d; y++) tile(x, y, '#6fb7d8')
    }
  }
  cache = { canvas: c, k, ox }
  return cache
}

export interface MinimapOpts {
  width: number
  me?: { x: number; y: number }
  discovered: Set<string>
  onPlace: (p: Place) => void
}

/** A minimap element: the cached town, district names, place markers and you. */
export function minimap(o: MinimapOpts): HTMLElement {
  const { canvas: bg, k, ox } = base(Math.round(o.width * Math.min(2, window.devicePixelRatio || 1)))
  const dpr = bg.width / o.width
  const wrap = document.createElement('div')
  wrap.className = 'minimap'
  const c = document.createElement('canvas')
  c.width = bg.width; c.height = bg.height
  c.style.width = `${o.width}px`
  c.style.height = `${bg.height / dpr}px`
  wrap.append(c)
  const ctx = c.getContext('2d')!
  const at = (x: number, y: number) => ({ x: (ox + (x - y) * k) / dpr, y: ((x + y) * k / 2) / dpr })
  let raf = 0
  const draw = (now: number) => {
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.clearRect(0, 0, c.width, c.height)
    ctx.drawImage(bg, 0, 0)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.font = '800 11px Nunito, ui-rounded, system-ui, sans-serif'
    ctx.textAlign = 'center'
    for (const d of DISTRICTS) {
      const p = at(d.x + d.w / 2, d.y + d.d / 2)
      ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(255,255,255,0.85)'; ctx.fillStyle = '#3a3552'
      ctx.strokeText(d.name, p.x, p.y - 4); ctx.fillText(d.name, p.x, p.y - 4)
    }
    if (o.me) {
      const p = at(o.me.x, o.me.y)
      const pulse = 0.5 + 0.5 * Math.sin(now / 300)
      ctx.fillStyle = `rgba(255,111,156,${0.25 + 0.2 * pulse})`; ctx.beginPath(); ctx.arc(p.x, p.y, 7 + pulse * 3, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = '#ff6f9c'; ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 2
      ctx.beginPath(); ctx.arc(p.x, p.y, 4.5, 0, Math.PI * 2); ctx.fill(); ctx.stroke()
    }
    raf = requestAnimationFrame(draw)
  }
  raf = requestAnimationFrame(draw)
  // Place markers are real buttons, so they are focusable and screen-reader friendly.
  for (const pl of ROOMS.town.places) {
    const p = at(pl.x + 0.5, pl.y + 0.5)
    const known = o.discovered.has(pl.id)
    const b = document.createElement('button')
    b.className = `pin${known ? '' : ' unknown'}`
    b.style.left = `${p.x}px`
    b.style.top = `${p.y}px`
    b.style.setProperty('--c', pl.accent)
    b.textContent = known ? pl.name : '?'
    b.title = known ? `${pl.name}: ${pl.blurb}` : 'Not discovered yet. Go and find it!'
    b.setAttribute('aria-label', known ? `Go to ${pl.name}` : 'Undiscovered place')
    if (known) b.addEventListener('click', () => o.onPlace(pl))
    else b.disabled = true
    wrap.append(b)
  }
  new MutationObserver((_m, obs) => { if (!wrap.isConnected) { cancelAnimationFrame(raf); obs.disconnect() } }).observe(document.body, { childList: true, subtree: true })
  return wrap
}
